'use client';

import { usePathname } from 'next/navigation';
import { RootLink } from 'components/link/link';
import { IconArrowLeft } from 'icon/Arrows/arrow-left';
import { useAtomValue } from 'jotai';
import type { FolderLink } from '../../_server/folders';
import { pathPrefixAtom } from '../../../_components/blog-state';
import {
  headerActionsAtom,
  headerLinksAtom,
} from '../../../_components/design-state';
import {
  getFolderNavigation,
  getFolderPathFromPathname,
  getPrefixedBlogHref,
} from '../folder-path';
import { SiteLogo } from './site-logo';

type BlogHeaderProps = {
  search: JSX.Element;
  folderLinks: FolderLink[];
};

export default function BlogHeader({ folderLinks, search }: BlogHeaderProps) {
  const headerActions = useAtomValue(headerActionsAtom);
  const headerLinks = useAtomValue(headerLinksAtom);
  const pathPrefix = useAtomValue(pathPrefixAtom);
  return (
    <header className="site-header">
      <div className="site-header-brand">
        <SiteLogo />
      </div>
      <FolderAwareNavigation
        folderLinks={folderLinks}
        headerLinks={headerLinks}
        pathPrefix={pathPrefix}
      />
      <div className="site-header-tools">
        <div className="site-search">{search}</div>
        <div className="site-header-actions">
          {headerActions.secondary?.name && (
            <ActionLink href={headerActions.secondary.href} kind="secondary">
              {headerActions.secondary.name}
            </ActionLink>
          )}
          {headerActions.primary?.name && (
            <ActionLink href={headerActions.primary.href} kind="primary">
              {headerActions.primary.name}
            </ActionLink>
          )}
        </div>
      </div>
    </header>
  );
}

function FolderAwareNavigation({
  folderLinks,
  headerLinks,
  pathPrefix,
}: {
  folderLinks: FolderLink[];
  headerLinks: Array<{ name: string; href: string }>;
  pathPrefix: string;
}) {
  const pathname = usePathname();
  const path = getFolderPathFromPathname(pathname, pathPrefix);

  return (
    <PathNavigation
      folderLinks={folderLinks}
      headerLinks={headerLinks}
      path={path}
      pathPrefix={pathPrefix}
    />
  );
}

function PathNavigation({
  folderLinks,
  headerLinks,
  path,
  pathPrefix,
}: {
  folderLinks: FolderLink[];
  headerLinks: Array<{ name: string; href: string }>;
  path: string[];
  pathPrefix: string;
}) {
  if (path.length === 0) {
    return (
      <Navigation
        folderLinks={folderLinks.filter(({ path }) => path.length === 1)}
        headerLinks={headerLinks}
        pathPrefix={pathPrefix}
      />
    );
  }

  const navigation = getFolderNavigation(path, folderLinks);
  return (
    <Navigation
      backLink={navigation.parent}
      folderLinks={navigation.children}
      headerLinks={[]}
      pathPrefix={pathPrefix}
    />
  );
}

function Navigation({
  backLink,
  folderLinks,
  headerLinks,
  pathPrefix,
}: {
  backLink?: FolderLink;
  folderLinks: FolderLink[];
  headerLinks: Array<{ name: string; href: string }>;
  pathPrefix: string;
}) {
  return (
    <nav className="site-navigation" aria-label="Primary navigation">
      {backLink ? (
        <>
          <RootLink
            aria-label={`Back to ${backLink.name}`}
            className="site-navigation-link site-folder-back"
            href={getPrefixedBlogHref(pathPrefix, backLink.href)}
          >
            <IconArrowLeft
              aria-hidden="true"
              className="site-folder-back-icon"
            />
            Back
          </RootLink>
          <FolderLinks folderLinks={folderLinks} pathPrefix={pathPrefix} />
        </>
      ) : null}
      {headerLinks.map((link) => (
        <RootLink
          className="site-navigation-link"
          href={link.href}
          key={link.name + link.href}
        >
          {link.name}
        </RootLink>
      ))}
      {backLink ? null : (
        <FolderLinks folderLinks={folderLinks} pathPrefix={pathPrefix} />
      )}
    </nav>
  );
}

function FolderLinks({
  folderLinks,
  pathPrefix,
}: {
  folderLinks: FolderLink[];
  pathPrefix: string;
}) {
  return folderLinks.map((folder) => (
    <RootLink
      className="site-navigation-link site-folder-link"
      href={getPrefixedBlogHref(pathPrefix, folder.href)}
      key={folder.href}
    >
      {folder.name}
    </RootLink>
  ));
}

function ActionLink({
  href,
  children,
  kind,
}: {
  href: string;
  children: React.ReactNode;
  kind: 'primary' | 'secondary';
}) {
  return (
    <RootLink className={`site-action site-action-${kind}`} href={href}>
      {children}
    </RootLink>
  );
}
